import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, ArrowRight, Inbox } from 'lucide-react';
import EmailBeacon from '../components/EmailBeacon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMsg, setResendMsg] = useState('');

  const email = location.state?.email || '';
  const rememberMe = location.state?.rememberMe || false;
  const password = location.state?.password || '';
  // Paid-plan visitors arrive here with the plan id captured from the
  // homepage pricing CTA (?plan=starter | plus_monthly | plus_annual).
  const intentPlan = location.state?.intentPlan || null;

  useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleContinue = async () => {
    setError('');
    setLoading(true);
    try {
      if (password) {
        await login(email, password, rememberMe);
        const paidPlans = ['starter', 'plus_monthly', 'plus_annual'];
        if (intentPlan && paidPlans.includes(intentPlan)) {
          navigate(`/pricing?plan=${intentPlan}`);
        } else {
          navigate('/');
        }
      } else {
        navigate('/login', { state: { message: 'Account created! Please log in.' } });
      }
    } catch (err) {
      setError('Sign-in failed. Please try logging in manually.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendCooldown(60);
    setResendMsg('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (r.ok) setResendMsg('Confirmation email re-sent — check your inbox.');
    } catch (_e) {
      setResendMsg('Could not resend right now. Please try again in a minute.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-44 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">Check Your Inbox</h1>
          <p className="text-gray-600 mt-2">
            We&apos;ve sent your welcome email and a confirmation link to<br />
            <strong data-testid="verify-email-address">{email}</strong>
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Live delivery beacon */}
          <div className="mb-6">
            <EmailBeacon email={email} />
          </div>

          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <Inbox size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              Click the <strong>confirmation link</strong> in the email when it arrives —
              you can carry on to your dashboard right away, and confirm any time.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <button
            onClick={handleContinue}
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            data-testid="continue-to-dashboard-btn"
          >
            {loading ? 'Signing you in…' : 'Continue to your dashboard'}
            {!loading && <ArrowRight size={16} />}
          </button>

          {/* Resend */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-2">Didn&apos;t receive anything?</p>
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              data-testid="resend-confirmation-btn"
            >
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend confirmation email'}
            </button>
            {resendMsg && <p className="text-xs text-emerald-700 mt-2" data-testid="resend-msg">{resendMsg}</p>}
          </div>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <a href="/login" className="text-sm text-gray-600 hover:text-gray-900">
              ← Back to Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
